import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query from JS.
 *
 * Used where a layout difference isn't just cosmetic — e.g. the home page
 * mounts the siteline feed *either* inline (mobile) *or* inside the floating
 * dock (desktop), never both, so the feed is fetched exactly once.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    // Safari <14 only has the deprecated addListener
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

export default useMediaQuery;
